if (!customElements.get('session-calendar')) {
  customElements.define(
    'session-calendar',
    class SessionCalendar extends HTMLElement {
      connectedCallback() {
        this.onRootClick = this.onRootClick.bind(this);
        this.onSlotChange = this.onSlotChange.bind(this);
        this.weekdayLabels = ['日', '一', '二', '三', '四', '五', '六'];
        this.cards = Array.from(this.querySelectorAll('[data-session-date]'));
        this.calRoot = this.querySelector('[data-session-cal-root]');
        this.dayLabel = this.querySelector('[data-session-day-label]');
        this.emptyNote = this.querySelector('[data-session-empty]');
        this.propDate = this.closest('product-info')?.querySelector('[data-session-prop-date]');
        this.propTime = this.closest('product-info')?.querySelector('[data-session-prop-time]');
        this.propWhole = this.closest('product-info')?.querySelector('[data-session-prop-whole]');
        this.qtyHint = this.closest('product-info')?.querySelector('[data-session-qty-hint]');
        this.kicker = this.closest('product-info')?.querySelector('[data-session-kicker]');
        this.defaultQtyHint = this.qtyHint?.dataset.sessionQtyHintDefault || '';
        this.defaultKicker = this.kicker?.dataset.sessionKickerDefault || '';

        const dates = [...new Set(this.cards.map((card) => card.dataset.sessionDate).filter(Boolean))].sort();
        this.availableDates = new Set(dates);

        const checked = this.querySelector('.session-slots__input:checked:not(.disabled)');
        this.selectedDate = checked?.closest('[data-session-date]')?.dataset.sessionDate || '';

        const seed = this.selectedDate || dates[0];
        if (seed) {
          const [year, month] = seed.split('-').map(Number);
          this.viewYear = year;
          this.viewMonth = month - 1;
        } else {
          const now = new Date();
          this.viewYear = now.getFullYear();
          this.viewMonth = now.getMonth();
        }

        this.renderCalendar();
        this.applyDateFilter(this.selectedDate, { announce: false });
        this.syncLineItemProperties();
        this.syncQtyConstraints({ resetValue: !checked });
        this.addEventListener('click', this.onRootClick);
        this.addEventListener('change', this.onSlotChange);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onRootClick);
        this.removeEventListener('change', this.onSlotChange);
      }

      onSlotChange(event) {
        if (!event.target.classList?.contains('session-slots__input')) return;
        const variantInput = this.closest('product-info')?.querySelector('input.product-variant-id');
        if (variantInput) variantInput.value = event.target.dataset.variantId || '';
        this.syncLineItemProperties();
        this.syncQtyConstraints({ resetValue: false });
        this.syncWholeBarCopy();
      }

      onRootClick(event) {
        const prev = event.target.closest('[data-session-cal-prev]');
        const next = event.target.closest('[data-session-cal-next]');
        const dayBtn = event.target.closest('[data-session-cal-day]');
        if (prev) {
          this.shiftMonth(-1);
          return;
        }
        if (next) {
          this.shiftMonth(1);
          return;
        }
        if (!dayBtn || dayBtn.disabled) return;
        const nextDate = dayBtn.dataset.sessionCalDay;
        if (nextDate !== this.selectedDate) {
          this.clearSlotSelection();
        }
        this.selectedDate = nextDate;
        this.renderCalendar();
        this.applyDateFilter(this.selectedDate, { announce: true });
        this.syncLineItemProperties();
        this.syncQtyConstraints({ resetValue: true });
        this.syncWholeBarCopy();
      }

      clearSlotSelection() {
        this.querySelectorAll('.session-slots__input:checked').forEach((input) => {
          input.checked = false;
        });
        const productInfo = this.closest('product-info');
        const variantInput = productInfo?.querySelector('input.product-variant-id');
        if (variantInput) variantInput.value = '';
        if (this.propDate) this.propDate.value = '';
        if (this.propTime) this.propTime.value = '';
        if (this.propWhole) {
          this.propWhole.value = '';
          this.propWhole.disabled = true;
        }
        this.syncWholeBarCopy();
        productInfo?.querySelector('product-form')?.toggleSubmitButton(false);
        if (productInfo?.dataset.updateUrl !== 'false') {
          const url = new URL(window.location.href);
          url.searchParams.delete('variant');
          window.history.replaceState({}, '', `${url.pathname}${url.search}`);
        }
      }

      qtyInput() {
        return this.closest('product-info')?.querySelector('.session-qty .quantity__input');
      }

      syncQtyConstraints({ resetValue }) {
        const qty = this.qtyInput();
        if (!qty) {
          this.syncWholeBarCopy();
          return;
        }
        const checked = this.querySelector('.session-slots__input:checked:not(.disabled)');
        const wrap =
          checked?.closest('[data-session-date]') ||
          (this.selectedDate
            ? this.querySelector(`[data-session-date="${this.selectedDate}"]`)
            : null);
        const minQty = parseInt(wrap?.dataset.sessionMinQty || '1', 10);
        const currentVal = parseInt(qty.value, 10);
        qty.min = String(minQty);
        qty.dataset.min = String(minQty);
        if (resetValue || Number.isNaN(currentVal) || currentVal < minQty) {
          qty.value = '';
        }
        this.syncWholeBarCopy();
      }

      syncWholeBarCopy() {
        const checked = this.querySelector('.session-slots__input:checked:not(.disabled)');
        const wrap = checked?.closest('[data-session-date]');
        const isWhole = wrap?.dataset.sessionWholeBar === 'true';
        if (this.qtyHint) {
          this.qtyHint.textContent = isWhole
            ? '包場最少 4 人、最多 6 人；訂金到場扣減，不設退款。Last order 00:00。'
            : this.defaultQtyHint;
        }
        if (this.kicker) {
          this.kicker.textContent = isWhole ? '包場 · 4.5 小時' : this.defaultKicker;
        }
      }

      shiftMonth(delta) {
        const date = new Date(this.viewYear, this.viewMonth + delta, 1);
        this.viewYear = date.getFullYear();
        this.viewMonth = date.getMonth();
        this.renderCalendar();
      }

      renderCalendar() {
        if (!this.calRoot) return;
        const first = new Date(this.viewYear, this.viewMonth, 1);
        const startWeekday = first.getDay();
        const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
        const cells = [];

        for (let i = 0; i < startWeekday; i += 1) {
          cells.push('<span class="session-cal__cell session-cal__cell--pad"></span>');
        }

        for (let day = 1; day <= daysInMonth; day += 1) {
          const iso = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const bookable = this.availableDates.has(iso);
          const selected = iso === this.selectedDate;
          const classes = ['session-cal__cell'];
          if (bookable) classes.push('session-cal__cell--open');
          if (selected) classes.push('is-selected');
          if (bookable) {
            cells.push(
              `<button type="button" class="${classes.join(' ')}" data-session-cal-day="${iso}" aria-pressed="${
                selected ? 'true' : 'false'
              }">${day}</button>`
            );
          } else {
            cells.push(`<span class="session-cal__cell session-cal__cell--muted">${day}</span>`);
          }
        }

        const title = `${this.viewYear}年${this.viewMonth + 1}月`;
        this.calRoot.innerHTML = `
          <div class="session-cal__nav">
            <button type="button" class="session-cal__nav-btn" data-session-cal-prev aria-label="上一個月">‹</button>
            <p class="session-cal__month">${title}</p>
            <button type="button" class="session-cal__nav-btn" data-session-cal-next aria-label="下一個月">›</button>
          </div>
          <div class="session-cal__weekdays">
            ${this.weekdayLabels.map((label) => `<span>${label}</span>`).join('')}
          </div>
          <div class="session-cal__grid">${cells.join('')}</div>
        `;
      }

      applyDateFilter(isoDate, { announce }) {
        this.cards.forEach((card) => {
          const match = Boolean(isoDate) && card.dataset.sessionDate === isoDate;
          card.hidden = !match;
        });
        if (this.dayLabel) {
          this.dayLabel.textContent = isoDate ? this.formatDate(isoDate) : '請選擇日期';
        }
        const visibleOpen = isoDate
          ? this.querySelectorAll(`[data-session-date="${isoDate}"] .session-slots__input:not(.disabled)`).length
          : 0;
        if (this.emptyNote) {
          this.emptyNote.hidden = !isoDate || visibleOpen > 0;
        }
        if (announce && this.dayLabel) {
          this.dayLabel.focus?.();
        }
      }

      formatDate(isoDate) {
        const [year, month, day] = isoDate.split('-').map(Number);
        const weekday = this.weekdayLabels[new Date(year, month - 1, day).getDay()];
        return `${year}年${month}月${day}日（週${weekday}）`;
      }

      syncLineItemProperties() {
        const checked = this.querySelector('.session-slots__input:checked:not(.disabled)');
        const wrap = checked?.closest('[data-session-date]');
        if (!wrap) {
          if (this.propDate) this.propDate.value = '';
          if (this.propTime) this.propTime.value = '';
          if (this.propWhole) {
            this.propWhole.value = '';
            this.propWhole.disabled = true;
          }
          return;
        }
        if (this.propDate) this.propDate.value = wrap.dataset.sessionDateLabel || wrap.dataset.sessionDate || '';
        if (this.propTime) this.propTime.value = wrap.dataset.sessionTimeLabel || '';
        const isWhole = wrap.dataset.sessionWholeBar === 'true';
        if (this.propWhole) {
          this.propWhole.disabled = !isWhole;
          this.propWhole.value = isWhole ? '是' : '';
        }
      }
    }
  );
}
